/**
 * Image URL rules shared by the sanitiser and by the surfaces that render an
 * image URL straight from a column (the cover on a card, the detail page, the
 * editor). Dependency-free on purpose: `ArticleCard` and `RichTextarea` import
 * this, and neither should drag a DOM emulator along.
 */

/**
 * The one host an article image may load from.
 *
 * Restricting `img src` is not about XSS — DOMPurify has already removed the
 * script vectors by this point. It is about **privacy and referrer leakage**: a
 * `<img>` pointing at an arbitrary host turns every reader of the article into
 * a request to that host, carrying their IP and, on some configurations, the
 * article URL. An author cannot opt every reader into that. Images are
 * uploaded, so they live on our storage host, so anything else is either a
 * mistake or a beacon.
 *
 * Derived from the configured Supabase URL rather than hardcoded, so it follows
 * the project across environments.
 */
export function storageHost(): string | null {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  if (!url) return null
  try {
    return new URL(url).host
  } catch {
    return null
  }
}

/**
 * The same host rule as the body `<img>` hook, for image URLs rendered outside
 * the sanitiser — the cover on the card, the detail page and the editor.
 *
 * `cover_image_url` is a plain column an author can PATCH to anything, and a
 * cover that loads from an arbitrary host is the reader beacon the body
 * restriction exists to prevent. Returns the URL only when it is absolute,
 * http(s), and on the storage host; otherwise null, and the caller renders
 * the placeholder cover instead.
 */
export function safeImageUrl(url: string | null | undefined): string | null {
  if (!url) return null
  const host = storageHost()
  if (!host) return null
  try {
    const u = new URL(url)
    return u.host === host && (u.protocol === 'https:' || u.protocol === 'http:') ? url : null
  } catch {
    return null
  }
}
